import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ExchangeRate, ExchangeRateService } from 'src/app/features';

export interface DayExchangeRate {
  day: number;
  dayName: string;
  buy: number;
  sell: number;
  hasData: boolean;
}

export interface MonthlyExchangeDialogData {
  date: Date;
}

@Component({
  selector: 'app-monthly-exchange-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './monthly-exchange-dialog.component.html',
  styleUrl: './monthly-exchange-dialog.component.scss',
})
export class MonthlyExchangeDialogComponent implements OnInit {
  dateCtrl = new FormControl<string>('');

  daysInMonth: DayExchangeRate[] = [];
  currentMonth: number;
  currentYear: number;
  selectedMonth: number;
  selectedYear: number;
  selectedMonthName: string = '';
  isLoadingMonth: boolean = false;

  months = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];

  dayNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];

  dateOptions: { value: string; label: string }[] = [];
  constructor(
    private exchangeRateService: ExchangeRateService,
    public dialogRef: MatDialogRef<MonthlyExchangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MonthlyExchangeDialogData
  ) {
    const today = new Date();
    this.currentMonth = today.getMonth() + 1;
    this.currentYear = today.getFullYear();

    const dialogDate = new Date(data.date);
    this.selectedMonth = dialogDate.getMonth() + 1;
    this.selectedYear = dialogDate.getFullYear();

    // Generar fecha 3 años antes y 3 años después
    this.generateDateOptions();

    // Inicializar el FormControl con el valor seleccionado
    const initialValue = `${this.selectedYear}-${this.selectedMonth.toString().padStart(2, '0')}`;
    this.dateCtrl.setValue(initialValue);
  }

  generateDateOptions(): void {
    this.dateOptions = [];
    for (let year = this.currentYear - 3; year <= this.currentYear + 3; year++) {
      for (let month = 1; month <= 12; month++) {
        const monthLabel = this.months.find(m => m.value === month)?.label || '';
        this.dateOptions.push({
          value: `${year}-${month.toString().padStart(2, '0')}`,
          label: `${monthLabel} ${year}`
        });
      }
    }
  }

  ngOnInit(): void {
    this.loadMonthData();
    this.dateCtrl.valueChanges.subscribe(() => this.loadMonthData());
  }

  loadMonthData(): void {
    this.isLoadingMonth = true;
    const dateValue = this.dateCtrl.value || '';
    const [yearStr, monthStr] = dateValue.split('-');
    const year = parseInt(yearStr, 10) || new Date().getFullYear();
    const month = parseInt(monthStr, 10) || 1;

    this.selectedMonth = month;
    this.selectedYear = year;
    this.selectedMonthName = this.months.find(m => m.value === month)?.label || '';

    this.exchangeRateService.getMonthlyByYear(month, year).subscribe(
      (data: {
        exchangeRatesByMonthYear: ExchangeRate[];
      }) => {
        const daysCount = new Date(year, month, 0).getDate();
        this.daysInMonth = [];
        const dataByDay: { [key: number]: ExchangeRate } = {};

        if (data.exchangeRatesByMonthYear) {
          data.exchangeRatesByMonthYear.forEach(rate => {
            const rateDate = new Date(`${rate.exchangeDate}T00:00:00`);
            const day = rateDate.getDate();
            dataByDay[day] = rate;
          });
        }

        for (let day = 1; day <= daysCount; day++) {
          const date = new Date(year, month - 1, day);
          const dayOfWeek = date.getDay();
          const rateForDay = dataByDay[day];
          this.daysInMonth.push({
            day,
            dayName: this.dayNames[dayOfWeek],
            buy: rateForDay ? rateForDay.buyRate : 0,
            sell: rateForDay ? rateForDay.sellRate : 0,
            hasData: !!rateForDay,
          });
        }
        this.isLoadingMonth = false;
      },
      (error) => {
        console.error('Error loading monthly exchange rates:', error);
        this.daysInMonth = [];
        this.isLoadingMonth = false;
      });
  }

  isCurrentDay(day: number): boolean {
    return (
      day === new Date().getDate() &&
      this.selectedMonth === this.currentMonth &&
      this.selectedYear === this.currentYear
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
